import { authFetch } from '../../api/csrf';
import { get, post } from "../apiClient";
import { API_BASE_URL } from "../config";

const NORMALIZED_API_BASE_URL = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : '';

const documentVerificationService = {
  // Get all documents for verification
  getDocuments: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.status) queryParams.append("status", filters.status);
      if (filters.documentType)
        queryParams.append("documentType", filters.documentType);
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.candidateId)
        queryParams.append("candidateId", filters.candidateId);
      if (filters.page) queryParams.append("page", filters.page);
      if (filters.limit) queryParams.append("limit", filters.limit);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);

      const url = `/document-verification/documents${queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      

      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.documents,
          total: response.data.total,
          pagination: response.data.pagination,
          filters: response.data.filters,
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || "Failed to fetch documents",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch documents",
      };
    }
  },

  // Verify a document (approve or reject)
  verifyDocument: async (documentId, action, notes = "") => {
    try {
      

      const response = await post(
        `/document-verification/documents/${documentId}/verify`,
        {
          action,
          notes,
        }
      );

      

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to verify document",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to verify document",
      };
    }
  },

  // Cleanup documents with wrong candidate associations
  cleanupDocuments: async () => {
    try {
      

      const response = await post("/document-verification/documents/cleanup");
      

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || "Failed to cleanup documents",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to cleanup documents",
      };
    }
  },

  // Download a document (cookie-based auth via authFetch)
  downloadDocument: async (documentId, options = {}) => {
    try {
      if (!NORMALIZED_API_BASE_URL) {
        throw new Error('API base URL is not configured. Please set REACT_APP_API_BASE_URL in your environment.');
      }

      const downloadUrl = `${NORMALIZED_API_BASE_URL}/document-verification/documents/${documentId}/download`;

      const response = await authFetch(downloadUrl, {
        method: 'GET',
        cache: 'no-store',
      });

      


      if (!response.ok) {
        let errorText = '';
        let errorData = null;

        try {
          errorText = await response.text();
          // Try to parse as JSON
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            // Not JSON, use as text
          }
        } catch (e) {
          errorText = 'Failed to download document';
        }

        // Handle 401 errors gracefully
        if (response.status === 401) {
          // Check if it's the specific "user no longer exists" error
          if (errorData?.responseMessage?.includes('user belonging to this token no longer exists') ||
            errorText.includes('user belonging to this token no longer exists')) {
            // Clear invalid auth data
            localStorage.removeItem('user_data');

            // Return a user-friendly error without the technical details
            return {
              success: false,
              error: 'Your session has expired. Please refresh the page and try again.',
              requiresLogin: true
            };
          }

          // Generic 401 error
          return {
            success: false,
            error: 'Authentication failed. Please refresh the page and try again.',
            requiresLogin: true
          };
        }

        // For other errors, show a generic message
        const errorMessage = errorData?.responseMessage ||
          errorData?.message ||
          errorText ||
          'Failed to download document';

        

        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or use documentId
      const sanitizeFileName = (name) => {
        if (!name || typeof name !== 'string') return '';
        return name
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
          .replace(/\s+/g, ' ')
          .trim();
      };

      const getExtensionFromMime = (mime) => {
        if (!mime) return '';
        if (mime.includes('pdf')) return '.pdf';
        if (mime.includes('png')) return '.png';
        if (mime.includes('jpeg')) return '.jpg';
        if (mime.includes('jpg')) return '.jpg';
        if (mime.includes('gif')) return '.gif';
        if (mime.includes('webp')) return '.webp';
        if (mime.includes('svg')) return '.svg';
        return '';
      };

      const ensureExtension = (name, mimeType, fallbackExt = '.pdf') => {
        if (!name) name = `${documentId}`;
        const hasExt = /\.[a-zA-Z0-9]+$/.test(name);
        if (hasExt) return name;
        const extFromMime = getExtensionFromMime(mimeType);
        if (extFromMime) return `${name}${extFromMime}`;
        if (options.fileName && /\.[a-zA-Z0-9]+$/.test(options.fileName)) {
          const ext = options.fileName.substring(options.fileName.lastIndexOf('.'));
          return `${name}${ext}`;
        }
        return `${name}${fallbackExt}`;
      };

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = '';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = sanitizeFileName(filenameMatch[1]);
        }
      }

      if (!filename && options.fileName) {
        filename = sanitizeFileName(options.fileName);
      }

      if (!filename) {
        filename = `${documentId}`;
      }

      // Create blob and download
      const blob = await response.blob();
      

      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty or corrupted');
      }

      // Check if blob is valid
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid file format received');
      }

      const mimeType = blob.type || options.fileType || response.headers.get('Content-Type');
      filename = ensureExtension(filename, mimeType);

      

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      
      return {
        success: true,
        message: "Document downloaded successfully",
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to download document",
      };
    }
  },

  // Get verification statistics
  getStatistics: async () => {
    try {
      

      const response = await get("/document-verification/statistics");
      

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch statistics",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch statistics",
      };
    }
  },

  // Fix isDocumentVerified field for a specific candidate
  fixCandidateVerification: async (candidateId) => {
    try {
      const response = await post(`/document-verification/fix-candidate/${candidateId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fix candidate verification',
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fix candidate verification',
      };
    }
  },

  // Manually set isDocumentVerified status for a candidate
  setCandidateVerificationStatus: async (candidateId, isDocumentVerified) => {
    try {
      const response = await post(`/document-verification/candidates/${candidateId}/verify-status`, {
        isDocumentVerified
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to set candidate verification status',
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to set candidate verification status',
      };
    }
  },
  fixCandidateVerification: async (candidateId) => {
    try {
      const response = await post(`/document-verification/fix-candidate/${candidateId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fix candidate verification',
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fix candidate verification',
      };
    }
  },

  // Manually set isDocumentVerified status for a candidate
  setCandidateVerificationStatus: async (candidateId, isDocumentVerified) => {
    try {
      const response = await post(`/document-verification/candidates/${candidateId}/verify-status`, {
        isDocumentVerified
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to set candidate verification status',
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to set candidate verification status',
      };
    }
  },

};

export default documentVerificationService;

